import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CheckCircle, XCircle, Settings2, ExternalLink, Trash2,
  Send, Eye, EyeOff, ChevronDown, ChevronUp, AlertCircle,
} from 'lucide-react';
import API, { formatError } from '../utils/api';
import { toast } from 'sonner';

// ─── Slack logo SVG ────────────────────────────────────────────────────────────
const SlackLogo = () => (
  <svg width="28" height="28" viewBox="0 0 124 124" fill="none">
    <path d="M26.4 78.2a13.2 13.2 0 1 1-13.2-13.2H26.4v13.2zm6.6 0a13.2 13.2 0 0 1 26.4 0v33a13.2 13.2 0 0 1-26.4 0v-33z" fill="#E01E5A"/>
    <path d="M45.8 26.4a13.2 13.2 0 1 1 13.2-13.2V26.4H45.8zm0 6.6a13.2 13.2 0 0 1 0 26.4H12.8a13.2 13.2 0 0 1 0-26.4h33z" fill="#36C5F0"/>
    <path d="M97.6 45.8a13.2 13.2 0 1 1 13.2 13.2H97.6V45.8zm-6.6 0a13.2 13.2 0 0 1-26.4 0v-33a13.2 13.2 0 0 1 26.4 0v33z" fill="#2EB67D"/>
    <path d="M78.2 97.6a13.2 13.2 0 1 1-13.2 13.2V97.6h13.2zm0-6.6a13.2 13.2 0 0 1 0-26.4h33a13.2 13.2 0 0 1 0 26.4h-33z" fill="#ECB22E"/>
  </svg>
);

// ─── Google Drive logo SVG ─────────────────────────────────────────────────────
const DriveLogo = () => (
  <svg width="28" height="28" viewBox="0 0 87.3 78" fill="none">
    <path d="M6.6 66.85 3.3 72.5c-.7 1.2-.1 2.7 1.2 3.2a28 28 0 0 0 5.3.3h67c1.8 0 3.5-.1 4.8-.3 1.3-.5 1.9-2 1.2-3.2l-3.3-5.65H6.6z" fill="#0066DA"/>
    <path d="M43.65 15.65 27.35 44l16.3 28.15L59.95 44l-16.3-28.35z" fill="#00AC47"/>
    <path d="M80.95 66.85l-21-36.35-16.3 28.35 16.3 8 21-8v8z" fill="#EA4335"/>
    <path d="M6.6 66.85l21-36.35 16.05-15L27.35 44 6.6 66.85z" fill="#00832D"/>
    <path d="M43.65 15.65l16.3-8 21 8-21 36.35-16.3-28.35z" fill="#2684FC"/>
    <path d="M6.6 66.85l20.75-22.85L43.65 15.65l-16.3 8L6.6 30.5l-3.3 36.35z" fill="#FFBA00"/>
  </svg>
);

const EVENTS = [
  { key: 'envelope.sent',      label: 'Document sent for signing' },
  { key: 'envelope.completed', label: 'All parties signed' },
  { key: 'signature.applied',  label: 'Individual signature applied' },
  { key: 'envelope.viewed',    label: 'Document viewed by signer' },
];

export default function Integrations() {
  const [searchParams] = useSearchParams();
  const [integrations, setIntegrations] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // 'slack' | 'google_drive' | null

  // Slack state
  const [slackForm, setSlackForm] = useState({ webhook_url: '', channel_name: '', events: ['envelope.sent','envelope.completed','signature.applied'], enabled: true });
  const [showWebhook, setShowWebhook] = useState(false);
  const [savingSlack, setSavingSlack] = useState(false);
  const [testingSlack, setTestingSlack] = useState(false);

  // Google Drive state
  const [driveForm, setDriveForm] = useState({ client_id: '', client_secret: '', auto_export: true, auto_archive: true, archive_folder_name: 'SignatureFlow/Completed' });
  const [showSecret, setShowSecret] = useState(false);
  const [savingDrive, setSavingDrive] = useState(false);
  const [connectingDrive, setConnectingDrive] = useState(false);

  useEffect(() => {
    const driveConnected = searchParams.get('drive_connected');
    const driveError = searchParams.get('drive_error');
    if (driveConnected) toast.success('Google Drive connected successfully!');
    if (driveError) toast.error(`Drive connection failed: ${driveError}`);
    load();
  }, []);

  const load = () => {
    API.get('/integrations')
      .then(r => {
        setIntegrations(r.data);
        if (r.data.slack?.config) {
          const c = r.data.slack.config;
          setSlackForm({ webhook_url: c.webhook_url || '', channel_name: c.channel_name || '', events: c.events || ['envelope.sent','envelope.completed','signature.applied'], enabled: r.data.slack.enabled ?? true });
        }
        if (r.data.google_drive?.config) {
          const c = r.data.google_drive.config;
          setDriveForm(p => ({ ...p, client_id: c.client_id || '', auto_export: c.auto_export ?? true, auto_archive: c.auto_archive ?? true, archive_folder_name: c.archive_folder_name || 'SignatureFlow/Completed' }));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const toggleEvent = (key) => setSlackForm(p => ({ ...p, events: p.events.includes(key) ? p.events.filter(e => e !== key) : [...p.events, key] }));

  const saveSlack = async (e) => {
    e.preventDefault();
    if (!slackForm.webhook_url.startsWith('https://hooks.slack.com/')) { toast.error('Enter a valid Slack Incoming Webhook URL'); return; }
    setSavingSlack(true);
    try {
      await API.put('/integrations/slack', slackForm);
      toast.success('Slack integration saved');
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSavingSlack(false); }
  };

  const testSlack = async () => {
    setTestingSlack(true);
    try { const r = await API.post('/integrations/slack/test'); toast.success(r.data.message); }
    catch (err) { toast.error(formatError(err)); }
    finally { setTestingSlack(false); }
  };

  const disconnectSlack = async () => {
    if (!window.confirm('Disconnect Slack?')) return;
    try { await API.delete('/integrations/slack'); toast.success('Slack disconnected'); load(); }
    catch (err) { toast.error(formatError(err)); }
  };

  const saveDriveCredentials = async (e) => {
    e.preventDefault();
    if (!driveForm.client_id || !driveForm.client_secret) { toast.error('Client ID and Secret are required'); return; }
    setSavingDrive(true);
    try {
      await API.put('/integrations/google-drive/credentials', driveForm);
      toast.success('Credentials saved. Click "Connect with Google" to authorize.');
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSavingDrive(false); }
  };

  const connectDrive = async () => {
    setConnectingDrive(true);
    try {
      const r = await API.get('/integrations/google-drive/connect');
      window.location.href = r.data.authorization_url;
    } catch (err) { toast.error(formatError(err)); setConnectingDrive(false); }
  };

  const disconnectDrive = async () => {
    if (!window.confirm('Disconnect Google Drive?')) return;
    try { await API.delete('/integrations/google-drive'); toast.success('Google Drive disconnected'); load(); }
    catch (err) { toast.error(formatError(err)); }
  };

  const saveDriveSettings = async () => {
    try {
      await API.put('/integrations/google-drive/settings', { auto_export: driveForm.auto_export, auto_archive: driveForm.auto_archive, archive_folder_name: driveForm.archive_folder_name });
      toast.success('Drive settings updated');
    } catch (err) { toast.error(formatError(err)); }
  };

  const slackConnected  = !!integrations.slack?.config?.webhook_url;
  const driveConnected  = integrations.google_drive?.config?.connected;
  const driveConfigured = !!integrations.google_drive?.config?.client_id;
  const connectedEmail  = integrations.google_drive?.config?.connected_email;

  if (loading) return <div className="p-8 flex justify-center"><div className="w-6 h-6 border-2 border-blue-700 border-t-transparent rounded-full animate-spin"/></div>;

  return (
    <div className="p-6 md:p-8 fade-in max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900" style={{fontFamily:'Outfit'}}>Integrations</h1>
        <p className="text-slate-500 text-sm mt-0.5">Connect SignatureFlow with your favourite tools</p>
      </div>

      <div className="space-y-4">

        {/* ── Google Drive ──────────────────────────────────────────────── */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="flex items-center gap-4 p-5">
            <div className="w-12 h-12 rounded-sm bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0">
              <DriveLogo />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-sm font-semibold text-slate-900">Google Drive</h2>
                {driveConnected ? (
                  <span className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-sm"><CheckCircle size={10}/>Connected{connectedEmail ? ` — ${connectedEmail}` : ''}</span>
                ) : driveConfigured ? (
                  <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-sm"><AlertCircle size={10}/>Not authorized</span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-sm"><XCircle size={10}/>Not connected</span>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-0.5">Import contracts, export signed docs, auto-archive completed agreements</p>
            </div>
            <button onClick={() => setExpanded(e => e === 'google_drive' ? null : 'google_drive')} className="flex items-center gap-1 text-xs text-slate-600 border border-slate-200 hover:bg-slate-50 px-3 py-1.5 rounded-sm transition-colors" data-testid="configure-drive-btn">
              <Settings2 size={12}/> Configure {expanded === 'google_drive' ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
            </button>
          </div>

          {expanded === 'google_drive' && (
            <div className="border-t border-slate-100 p-5 bg-slate-50 space-y-5">
              {!driveConnected && (
                <form onSubmit={saveDriveCredentials} className="space-y-4">
                  <div className="bg-blue-50 border border-blue-100 rounded-sm px-4 py-3 text-xs text-blue-700">
                    <strong>Setup:</strong> Go to <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer" className="underline font-medium">Google Cloud Console</a> → Enable Drive API → Create OAuth 2.0 credentials (Web app type). Set redirect URI to: <code className="bg-blue-100 px-1 rounded">{window.location.origin}/api/oauth/drive/callback</code>
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">OAuth Client ID</label>
                      <input type="text" value={driveForm.client_id} onChange={e => setDriveForm(p=>({...p,client_id:e.target.value}))} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="xxxx.apps.googleusercontent.com" data-testid="drive-client-id-input"/>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">OAuth Client Secret</label>
                      <div className="relative">
                        <input type={showSecret?'text':'password'} value={driveForm.client_secret} onChange={e => setDriveForm(p=>({...p,client_secret:e.target.value}))} className="w-full px-3 py-2 pr-9 border border-slate-300 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="GOCSPX-..." data-testid="drive-client-secret-input"/>
                        <button type="button" onClick={()=>setShowSecret(p=>!p)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400">{showSecret?<EyeOff size={13}/>:<Eye size={13}/>}</button>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button type="submit" disabled={savingDrive} className="flex items-center gap-1.5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-2 rounded-sm text-xs font-medium transition-colors disabled:opacity-60" data-testid="save-drive-credentials-btn">
                      {savingDrive?'Saving...':'Save Credentials'}
                    </button>
                    {driveConfigured && (
                      <button type="button" onClick={connectDrive} disabled={connectingDrive} className="flex items-center gap-1.5 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-sm text-xs font-medium transition-colors disabled:opacity-60" data-testid="connect-drive-btn">
                        <ExternalLink size={12}/> {connectingDrive?'Redirecting...':'Connect with Google'}
                      </button>
                    )}
                  </div>
                </form>
              )}

              {driveConnected && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-slate-700">Connected as</p>
                      <p className="text-sm text-slate-900 mt-0.5">{connectedEmail}</p>
                    </div>
                    <button onClick={disconnectDrive} className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 border border-red-200 hover:bg-red-50 px-3 py-1.5 rounded-sm transition-colors" data-testid="disconnect-drive-btn">
                      <Trash2 size={11}/> Disconnect
                    </button>
                  </div>
                  <div className="space-y-3 pt-3 border-t border-slate-200">
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Automation Settings</p>
                    {[
                      {key:'auto_export', label:'Auto-export signed contracts to Drive', desc:'Export the document when an envelope is completed'},
                      {key:'auto_archive', label:'Auto-archive to folder', desc:'Save completed agreements in a Drive folder'},
                    ].map(({key,label,desc}) => (
                      <label key={key} className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" checked={driveForm[key]} onChange={e=>setDriveForm(p=>({...p,[key]:e.target.checked}))} className="mt-0.5 w-4 h-4 accent-blue-700"/>
                        <div><p className="text-sm text-slate-700">{label}</p><p className="text-xs text-slate-400">{desc}</p></div>
                      </label>
                    ))}
                    {driveForm.auto_archive && (
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Archive Folder Name</label>
                        <input type="text" value={driveForm.archive_folder_name} onChange={e=>setDriveForm(p=>({...p,archive_folder_name:e.target.value}))} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="drive-folder-input"/>
                      </div>
                    )}
                    <button onClick={saveDriveSettings} className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-sm text-xs font-medium transition-colors" data-testid="save-drive-settings-btn">
                      Save Settings
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Slack ──────────────────────────────────────────────────────── */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="flex items-center gap-4 p-5">
            <div className="w-12 h-12 rounded-sm bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0">
              <SlackLogo />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-sm font-semibold text-slate-900">Slack</h2>
                {slackConnected ? (
                  <span className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-sm"><CheckCircle size={10}/>Configured{integrations.slack?.config?.channel_name ? ` — ${integrations.slack.config.channel_name}` : ''}</span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-sm"><XCircle size={10}/>Not connected</span>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-0.5">Get real-time notifications in Slack when documents are sent, signed, or completed</p>
            </div>
            <button onClick={() => setExpanded(e => e === 'slack' ? null : 'slack')} className="flex items-center gap-1 text-xs text-slate-600 border border-slate-200 hover:bg-slate-50 px-3 py-1.5 rounded-sm transition-colors" data-testid="configure-slack-btn">
              <Settings2 size={12}/> Configure {expanded === 'slack' ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
            </button>
          </div>

          {expanded === 'slack' && (
            <div className="border-t border-slate-100 p-5 bg-slate-50 space-y-5">
              <div className="bg-amber-50 border border-amber-100 rounded-sm px-4 py-3 text-xs text-amber-700">
                <strong>Setup:</strong> In your Slack workspace go to <a href="https://api.slack.com/messaging/webhooks" target="_blank" rel="noreferrer" className="underline font-medium">Incoming Webhooks</a> → Create a new webhook → Copy the URL below.
              </div>
              <form onSubmit={saveSlack} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Incoming Webhook URL</label>
                  <div className="relative">
                    <input type={showWebhook?'text':'password'} value={slackForm.webhook_url} onChange={e=>setSlackForm(p=>({...p,webhook_url:e.target.value}))} className="w-full px-3 py-2 pr-9 border border-slate-300 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="https://hooks.slack.com/services/..." data-testid="slack-webhook-input"/>
                    <button type="button" onClick={()=>setShowWebhook(p=>!p)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400">{showWebhook?<EyeOff size={13}/>:<Eye size={13}/>}</button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Channel Name <span className="text-slate-400 font-normal">(for display only)</span></label>
                  <input type="text" value={slackForm.channel_name} onChange={e=>setSlackForm(p=>({...p,channel_name:e.target.value}))} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="#legal-docs" data-testid="slack-channel-input"/>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Notify on</label>
                  <div className="space-y-2">
                    {EVENTS.map(({key,label}) => (
                      <label key={key} className="flex items-center gap-2.5 cursor-pointer">
                        <input type="checkbox" checked={slackForm.events.includes(key)} onChange={()=>toggleEvent(key)} className="w-4 h-4 accent-blue-700" data-testid={`slack-event-${key}`}/>
                        <span className="text-sm text-slate-700">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={slackForm.enabled} onChange={e=>setSlackForm(p=>({...p,enabled:e.target.checked}))} className="w-4 h-4 accent-blue-700"/>
                    <span className="text-xs text-slate-600">Enabled</span>
                  </label>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <button type="submit" disabled={savingSlack} className="flex items-center gap-1.5 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-sm text-xs font-medium transition-colors disabled:opacity-60" data-testid="save-slack-btn">
                    {savingSlack?'Saving...':'Save Integration'}
                  </button>
                  {slackConnected && (
                    <>
                      <button type="button" onClick={testSlack} disabled={testingSlack} className="flex items-center gap-1.5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-2 rounded-sm text-xs font-medium transition-colors disabled:opacity-60" data-testid="test-slack-btn">
                        <Send size={11}/> {testingSlack?'Sending...':'Test'}
                      </button>
                      <button type="button" onClick={disconnectSlack} className="flex items-center gap-1.5 text-red-500 hover:text-red-700 border border-red-200 hover:bg-red-50 px-3 py-2 rounded-sm text-xs font-medium transition-colors ml-auto" data-testid="disconnect-slack-btn">
                        <Trash2 size={11}/> Disconnect
                      </button>
                    </>
                  )}
                </div>
              </form>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
